import { Search, FileText, Video, Link } from "lucide-react";
import { useState, useRef, useEffect, useMemo } from "react";
import Markdown from "../ui-common/markdown";
import { formatDate } from "../../lib/utils";
import VideoPopup from "./video_popup.jsx";

const sourceTypeConfig = {
  lectures: {
    icon: <Video className="w-3.5 h-3.5 text-muted-foreground" />,
    make_title: (result) => `${result.lecture_name}`,
    make_metadata: (result) => `${result.host_name} • ${formatDate(result.upload_date, {useAlphabetDate: true})} • Starting from: ${result.timestamp_start}`,
    make_link_component: (result) => (
      <VideoPopup panoptoURL={result.remote_url} title={result.lecture_name}>
        {result.lecture_name}
      </VideoPopup>
    ),
    label: "Lecture",
  },
  moodle: {
    icon: <FileText className="w-3.5 h-3.5 text-muted-foreground" />,
    make_title: (result) => `${result.file_name}`,
    make_metadata: (result) => `${formatDate(result.upload_date, {useAlphabetDate: true})} • Page ${result.page}/${result.total_pages} `,
    make_link_component: (result) => (
      <a href={result.remote_url} target="_blank" rel="noopener noreferrer" className="hover:underline">
        {result.file_name}
      </a>
    ),
    label: "Document",
  },
};

const HighlightedText = ({ children }) => (
  <mark
    className="text-primary bg-primary/10 p-0.5 rounded-sm"
  >
    <Markdown>{children}</Markdown>
  </mark>
);


/**
 * A helper function that takes a text segment and splits it into
 * a leading space, core text, and trailing space.
 * (This is a hack to tackle whitespace issues with inline elements.)
 * @returns {React.ReactElement[]} An array of components.
 */
const processNonMatch = (segment, baseKey) => {
  if (!segment) {
    return []; // Return an empty array if the segment is empty
  }

  const elements = [];
  let coreText = segment;

  // 1. Check for and handle a leading space
  if (coreText.startsWith(' ')) {
    elements.push(<span key={`${baseKey}-ls`}>{' '}</span>);
    coreText = coreText.slice(1);
  }

  // 2. The remaining text might also have a trailing space
  const hasTrailingSpace = coreText.endsWith(' ');
  if (hasTrailingSpace) {
    coreText = coreText.slice(0, -1);
  }
  
  // 3. Push the core text, if any remains
  if (coreText) {
    elements.push(<Markdown key={`${baseKey}-core`}>{coreText}</Markdown>);
  }

  // 4. If there was a trailing space, add it now
  if (hasTrailingSpace) {
    elements.push(<span key={`${baseKey}-ts`}>{' '}</span>);
  }

  return elements;
};

const textToHighlightedComponents = (text, keywords) => {
  if (!keywords || !text || keywords.length === 0) {
    return <p><Markdown>{text}</Markdown></p>;
  }

  // Escape special regex characters in keywords and build a case-insensitive regex
  // This regex will match any of the keywords in the text, regardless of case
  const escapedKeywords = keywords.map(kw => kw.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'));
  const regex = new RegExp(`(${escapedKeywords.join('|')})`, 'gi');

  // Build the React component list, while highlighting matches
  const elements = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Process the non-matching text before the highlight
    const nonMatchSegment = text.slice(lastIndex, match.index);
    elements.push(...processNonMatch(nonMatchSegment, `text-${lastIndex}`));

    // Push the highlight itself
    const matchSegment = match[0];
    elements.push(<HighlightedText key={`match-${match.index}`}>{matchSegment}</HighlightedText>);

    // Update the lastIndex to the end of the current match
    lastIndex = regex.lastIndex;
  }

  // Process the final non-matching text after the last highlight
  const remainingSegment = text.slice(lastIndex);
  elements.push(...processNonMatch(remainingSegment, `text-${lastIndex}`));

  return <p>{elements}</p>;
};


const SearchResultItem = ({ result, index, keywords }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    if (textRef.current) {
      setIsTruncated(textRef.current.scrollHeight > textRef.current.clientHeight);
    }
  }, [result.raw_text]);

  const config = sourceTypeConfig[result.src_group] || {};

  return (
    <div
      key={index}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, ease: "easeOut" }}
      className="mb-3"
    >
      <div className="bg-transparent py-2 px-2 rounded-lg border-l-2 border-transparent hover:border-primary/70 hover:bg-accent/50 transition-colors duration-200">
        <h3 className="text-sm font-medium text-foreground/90 mb-1 flex items-center">
          {config.make_link_component(result)}
          <Link className="w-3 h-3 text-muted-foreground ml-1.5" />
        </h3>

        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
          <div className="inline-flex items-center gap-1.5 rounded-md bg-muted/70 text-muted-foreground capitalize px-2 py-0.5 text-[10px] font-medium">
            {config.icon}
            {result.sourceType}
          </div>
          <span>{config.make_metadata(result)}</span>
        </div>

        <div
          className={`overflow-hidden cursor-pointer transition-all duration-300 ease-in-out ${isExpanded ? "max-h-none" : "max-h-[3rem]"}`}
          onClick={() => isTruncated && !isExpanded && setIsExpanded(true)}
        >
          <div
            ref={textRef}
            className={`text-foreground/70 text-sm leading-relaxed ${!isExpanded ? "line-clamp-2" : ""}`}>
            {textToHighlightedComponents(result.raw_text, keywords)}
          </div>
        </div>
        {isExpanded && (
          <button
            onClick={() => setIsExpanded(false)}
            className="text-xs text-primary/80 hover:underline mt-1"
          >
            Show Less
          </button>
        )}
      </div>
    </div>
  );
};

export { SearchResultItem };